/*
 * WebMCP bootstrap: builds the registry contents and connects the browser transport.
 *
 * Import this once from the application entry point. Everything the agent can do
 * is registered here, so this file is the map of the app's agent surface.
 */

import { toolRegistry } from './toolRegistry.js'
import { WebMcpAdapter, WebMcpTransport } from './adapter.js'
import { createAppStatusTool } from './tools/appStatus.js'
import { createEditorTools } from './tools/editorTools.js'
import { editorHost } from '../editor/editorHost.js'
import { EditorCommands } from '../editor/editorCommands.js'
import { documentRevision } from '../editor/revision.js'

export { toolRegistry, WebMcpAdapter, WebMcpTransport, editorHost }

export const webMcpAdapter = new WebMcpAdapter(toolRegistry)
export const editorCommands = new EditorCommands(editorHost, documentRevision)

let initPromise = null

/**
 * Register every tool this application exposes. Idempotent.
 *
 * The editor tools are registered unconditionally, even before the editor page has
 * been opened: an agent must be able to *see* the capability and get a clear
 * EDITOR_NOT_READY error telling it what to do, rather than find the tool missing.
 */
export function registerApplicationTools (registry = toolRegistry) {
  const definitions = [
    createAppStatusTool({ adapter: webMcpAdapter }),
    ...createEditorTools(editorCommands)
  ]
  for (const definition of definitions) {
    if (!registry.has(definition.name)) registry.register(definition)
  }
  return registry
}

/**
 * Called once at startup. Safe in any browser: if WebMCP is missing the
 * application keeps running and the console explains why.
 */
export function initWebMcp (options) {
  if (!initPromise) {
    registerApplicationTools()
    initPromise = webMcpAdapter.connect(options)

    // A stable, documented handle for the debug panel and for Playwright.
    // It exposes only capabilities that are, by design, already exposed to an
    // agent through WebMCP on this page — no secrets, no privileged operations.
    if (typeof window !== 'undefined') {
      window.__WEBMCP__ = {
        registry: toolRegistry,
        adapter: webMcpAdapter,
        editor: editorHost,
        commands: editorCommands,
        revision: documentRevision,
        invoke: (name, args) => toolRegistry.invoke(name, args, { source: 'test-harness' }),
        listTools: () => toolRegistry.list(),
        status: () => webMcpAdapter.status(),
        ready: initPromise
      }
    }
  }
  return initPromise
}
